<template>
	<div ref="el" class="canvas-wrapper" data-component="shader-canvas">
		<div class="canvas-container">
			<canvas id="webgl-canvas" />
		</div>
		<slot />
	</div>
</template>

<script setup>
import { WebGLRenderer, Vector2, Color, OrthographicCamera, Scene, Mesh, PlaneGeometry, ShaderMaterial } from 'three'
import gsap from 'gsap'

import { useEmitter } from '~/composables/core'

import vertexShader from './shaders/vertex.vert'
import goldFragmentShader from './shaders/gold.frag'

const context = inject('pageContext')
const el = ref(null)

const emitter = useEmitter()

//##################################################

let canvas, renderer
let mainCamera
let scenes = []
let planeGeometry

let startTime = Date.now()
let lastFrameTime = Date.now()

let padding = 0.2
let viewportHeight, viewportWidth

let canvasPageTop = 0
const cachedSceneBounds = []
let verticalBuffer = 0

let mouseX = 0
let mouseY = 0
let lastMouseX = 0
let lastMouseY = 0

let sceneVelocities = {}

//##################################################

const triggerEnterAnim = () => {
	const targetScene = scenes.find((scene) => scene.userData.name === 'gold-header')
	if (!targetScene) return

	const aux = targetScene.mesh.material.uniforms.rotateSpeed.value

	gsap.fromTo(
		targetScene.mesh.material.uniforms.uZoom,
		{
			value: 3.0,
			ease: 'expo.out',
		},
		{
			duration: 3.0,
			value: 1.0,
			ease: 'expo.out',
		}
	)

	gsap.fromTo(
		targetScene.mesh.material.uniforms.rotateSpeed,
		{
			value: -1.0,
			ease: 'expo.out',
		},
		{
			duration: 2.0,
			value: aux,
			ease: 'expo.out',
		}
	)
}

const handleMouseEnter = (shaderName) => {
	const targetScene = scenes.find((scene) => scene.userData.name === shaderName)
	if (!targetScene) return

	gsap.to(targetScene.mesh.material.uniforms.uZoom, {
		duration: 2.0,
		value: shaderName.includes('card') ? 2.0 : 1.2,
		ease: 'expo.out',
		overwrite: true,
	})

	sceneVelocities[shaderName].current = shaderName.includes('card') ? 1.2 : 2.0
}

const handleMouseLeave = (shaderName) => {
	const targetScene = scenes.find((scene) => scene.userData.name === shaderName)
	if (!targetScene) return

	gsap.to(targetScene.mesh.material.uniforms.uZoom, {
		duration: 2.0,
		value: 1.0,
		ease: 'expo.out',
		overwrite: true,
	})

	sceneVelocities[shaderName].current = shaderName.includes('card') ? 1.2 : 2.0
}
emitter.on('shaderCanvas-mouseEnter', handleMouseEnter)
emitter.on('shaderCanvas-mouseLeave', handleMouseLeave)

//##################################################

function handleMouseMove(e) {
	mouseX = e.clientX
	mouseY = e.clientY
}

function updateRectBounds() {
	const currentScroll = window.scrollY

	const wrapperRect = el.value.getBoundingClientRect()
	canvasPageTop = wrapperRect.top + currentScroll + canvas.offsetTop

	scenes.forEach((scene) => {
		const element = scene.userData.element
		if (element) {
			const rect = element.getBoundingClientRect()

			cachedSceneBounds[scene.userData.index] = {
				pageTop: rect.top + currentScroll,
				pageLeft: rect.left + window.scrollX,
				width: rect.width,
				height: rect.height,
				left: rect.left,
			}
		}
	})
}

function handleResize() {
	viewportWidth = window.innerWidth
	viewportHeight = window.innerHeight

	const canvasHeight = viewportHeight * (1 + padding * 2)

	canvas.style.width = `${viewportWidth}px`
	canvas.style.height = `${canvasHeight}px`

	renderer.setSize(viewportWidth, canvasHeight, false)

	updateRectBounds()

	verticalBuffer = padding * viewportHeight

	scenes.forEach((scene) => {
		const bounds = cachedSceneBounds[scene.userData.index]
		if (scene.mesh?.material?.uniforms?.iResolution && bounds) {
			scene.mesh.material.uniforms.iResolution.value.set(bounds.width, bounds.height)
		}
	})
}

function createAnimatedShaderMaterial(type) {
	let fragmentShader

	const uniforms = {
		iTime: { value: 0.0 },
		iResolution: { value: new Vector2() },
	}

	switch (type) {
		case 'gold-header':
		case 'gold-footer':
			fragmentShader = goldFragmentShader
			uniforms.cornerRadius = { value: 350 }
			uniforms.uSeed = { value: Math.random() }
			uniforms.uZoom = { value: 1.0 }
			uniforms.rotateSpeed = { value: 0.05 }
			uniforms.uOffsetX = { value: type == 'gold-header' ? 0.1 : 0.01 }
			uniforms.uOffsetY = { value: type == 'gold-header' ? 0.05 : 0.03 }
			uniforms.grainSize = { value: 0.5 }
			uniforms.grainIntensity = { value: 0.35 }
			uniforms.gradientSize = { value: 0.01 }
			uniforms.gradientVelocity = { value: 0.05 }
			uniforms.gradientSpeed = { value: 0.0 }
			uniforms.uColor1 = { value: new Color(1.0, 0.588, 0.047) }
			uniforms.uColor2 = { value: new Color(0.059, 0.039, 0.031) }
			uniforms.uColor3 = { value: new Color(0.627, 0.376, 0.031) }
			break
		case 'gold-card-even':
			fragmentShader = goldFragmentShader
			uniforms.cornerRadius = { value: 100 }
			uniforms.uSeed = { value: Math.random() }
			uniforms.uZoom = { value: 1.0 }
			uniforms.rotateSpeed = { value: 0.04 }
			uniforms.uOffsetX = { value: 0.0 }
			uniforms.uOffsetY = { value: 0.0 }
			uniforms.grainSize = { value: 0.5 }
			uniforms.grainIntensity = { value: 0.35 }
			uniforms.gradientSize = { value: 0.01 }
			uniforms.gradientVelocity = { value: 0.1 }
			uniforms.gradientSpeed = { value: 0.0 }
			uniforms.uColor1 = { value: new Color(1.0, 0.588, 0.047) }
			uniforms.uColor2 = { value: new Color(0.125, 0.07, 0.047) }
			uniforms.uColor3 = { value: new Color(0.627, 0.376, 0.031) }
			break
		case 'gold-card-odd':
			fragmentShader = goldFragmentShader
			uniforms.cornerRadius = { value: 100 }
			uniforms.uSeed = { value: Math.random() * 100.0 + 10000.0 }
			uniforms.uZoom = { value: 1.0 }
			uniforms.rotateSpeed = { value: -0.03 }
			uniforms.uOffsetX = { value: 0.0 }
			uniforms.uOffsetY = { value: 0.0 }
			uniforms.grainSize = { value: 0.5 }
			uniforms.grainIntensity = { value: 0.35 }
			uniforms.gradientSize = { value: 0.015 }
			uniforms.gradientVelocity = { value: 0.15 }
			uniforms.gradientSpeed = { value: 0.0 }
			uniforms.uColor1 = { value: new Color(1.0, 0.588, 0.047) }
			uniforms.uColor2 = { value: new Color(0.125, 0.07, 0.047) }
			uniforms.uColor3 = { value: new Color(0.627, 0.376, 0.031) }
			break
	}

	return new ShaderMaterial({
		vertexShader: vertexShader,
		fragmentShader: fragmentShader,
		uniforms: uniforms,
	})
}

function animate() {
	const currentTime = Date.now()
	const elapsedTime = (currentTime - startTime) / 1000
	const deltaTime = (currentTime - lastFrameTime) / 1000
	lastFrameTime = currentTime

	const currentScroll = window.scrollY
	canvas.style.transform = `translateY(${currentScroll}px)`

	renderer.setScissorTest(false)
	renderer.clear()
	renderer.setScissorTest(true)

	scenes.forEach(function (scene) {
		const element = scene.userData.element
		if (!element) return

		const bounds = cachedSceneBounds[scene.userData.index]
		if (!bounds) return

		const canvasHeight = canvas.clientHeight

		const sceneLeft = bounds.pageLeft
		const sceneTop = bounds.pageTop - (canvasPageTop + currentScroll)
		const sceneBottom = canvasHeight - (sceneTop + bounds.height)
		const sceneWidth = bounds.width
		const sceneHeight = bounds.height

		const viewportRelativeTop = bounds.pageTop - currentScroll - viewportHeight * padding

		if (viewportRelativeTop + bounds.height < -verticalBuffer || viewportRelativeTop > viewportHeight + verticalBuffer) return

		const sceneName = scene.userData.name
		if (!sceneVelocities[sceneName]) {
			sceneVelocities[sceneName] = {
				current: 0.05,
				target: 0.05,
				mouseVelocity: 0,
			}
		}

		const velocity = sceneVelocities[sceneName]

		const baseVelocity = 0.05

		if (sceneName === 'gold-header') {
			const dx = mouseX - lastMouseX
			const dy = mouseY - lastMouseY
			const distance = Math.sqrt(dx * dx + dy * dy)
			velocity.mouseVelocity = distance / Math.max(deltaTime, 0.016)

			lastMouseX = mouseX
			lastMouseY = mouseY

			const velocityBoost = Math.min(velocity.mouseVelocity * 0.2, 0.7)

			velocity.target = baseVelocity + velocityBoost
		}

		const velocityDiff = velocity.target - velocity.current
		if (Math.abs(velocityDiff) > 0.01) {
			const lerpFactor = 1.0 - Math.exp(-deltaTime * 3.0)
			velocity.current += velocityDiff * lerpFactor
		} else velocity.current = velocity.target

		if (velocity.mouseVelocity > 0.01) velocity.mouseVelocity *= Math.exp(-deltaTime * 5.0)
		else velocity.mouseVelocity = 0

		renderer.setViewport(sceneLeft, sceneBottom, sceneWidth, sceneHeight)
		renderer.setScissor(sceneLeft, sceneBottom, sceneWidth, sceneHeight)

		if (scene.mesh?.material?.uniforms?.iTime) scene.mesh.material.uniforms.iTime.value = elapsedTime
		if (scene.mesh?.material?.uniforms?.gradientSpeed) scene.mesh.material.uniforms.gradientSpeed.value += velocity.current * deltaTime

		renderer.render(scene, mainCamera)
	})
}

const setup = () => {
	canvas = document.getElementById('webgl-canvas')

	planeGeometry = new PlaneGeometry(2, 2)

	// create single shared camera
	mainCamera = new OrthographicCamera(-1, 1, 1, -1, 1, 10)
	mainCamera.position.z = 1

	// create single shared renderer
	renderer = new WebGLRenderer({ canvas: canvas, antialias: true })
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1))
	renderer.debug.checkShaderErrors = false
	renderer.setAnimationLoop(animate)
	renderer.setClearAlpha(0)

	const sceneElements = document.querySelectorAll('[data-shader]')

	sceneElements.forEach((sceneElement, i) => {
		const scene = new Scene()
		scene.userData.element = sceneElement
		scene.userData.index = i
		scene.userData.name = sceneElement.dataset.shader

		const shaderType = sceneElement.dataset.shader

		const material = createAnimatedShaderMaterial(shaderType)

		const mainMesh = new Mesh(planeGeometry, material)
		scene.add(mainMesh)

		scene.mesh = mainMesh
		scenes.push(scene)
	})

	setTimeout(handleResize, 100)

	window.addEventListener('resize', onResize)
	window.addEventListener('mousemove', handleMouseMove)

	canvas.classList.add('is-loaded')

	triggerEnterAnim()
}

const onResize = () => {
	handleResize()
	setTimeout(handleResize, 500)
}

const kill = () => {
	window.removeEventListener('resize', onResize)
	window.removeEventListener('mousemove', handleMouseMove)

	emitter.off('shaderCanvas-mouseEnter', handleMouseEnter)
	emitter.off('shaderCanvas-mouseLeave', handleMouseLeave)

	if (renderer) {
		renderer.setAnimationLoop(null)
		renderer.dispose()
		renderer = null
	}

	if (scenes) {
		scenes.forEach((scene) => {
			if (scene.mesh?.material) scene.mesh.material.dispose()

			scene.remove(scene.mesh)
			scene.mesh = null
			scene.userData = null
		})
		scenes = []
	}

	if (planeGeometry) {
		planeGeometry.dispose()
		planeGeometry = null
	}

	// sceneVelocities = {}
	// cachedSceneBounds.length = 0
}

onMounted(() => {
	context.$page.loader.ready.then(() => setup())
})

onBeforeUnmount(() => {
	kill()
})
</script>

<style lang="scss" scoped>
[data-component='shader-canvas'] {
	position: relative;

	.canvas-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
		z-index: -1;
		pointer-events: none;
	}

	#webgl-canvas {
		position: absolute;
		left: 0;
		top: -20vh;

		width: 100%;
		height: 100%;

		will-change: transform;

		z-index: -1;

		opacity: 0;

		transition: opacity 2s ease(out-quint) 0.3s;

		&.is-loaded {
			opacity: 1;
		}
	}
}
</style>
